/**
 * Agrupa e rotula itens do inventário (feature-map.csv) para a Central de Ajuda.
 * Evita duplicatas por rota técnica e substitui nomes internos (ChatInbox, Products…) por português.
 */

/** Nomes internos de página → rótulo visível ao usuário. */
export const PAGE_DISPLAY_LABELS = {
  ChatInbox: "Lista de conversas",
  Conversation: "Dentro da conversa",
  ChatPopup: "Janela separada (popup)",
  Realtime: "Atualização em tempo real",
  SalesPipeline: "Pipeline de Vendas",
  AwarenessPipeline: "Níveis de consciência",
  Products: "Produtos",
  Surveys: "Pesquisas",
  SurveyAnalytics: "Análise da pesquisa",
  Campaigns: "Campanhas",
  Dashboard: "Dashboard",
  DashboardInsights: "Resumos automáticos do Dashboard",
  Clients: "Clientes",
  ClientDetails: "Detalhes do cliente",
  SearchResults: "Resultados da busca",
  AgentsList: "Lista de agentes de IA",
  AgentForm: "Cadastro do agente de IA",
  AgentAnalytics: "Estatísticas do agente de IA",
  Channels: "Canais",
  Users: "Usuários",
  BotFlowEditor: "Fluxo do chatbot por usuário",
  Companies: "Empresas",
  Positions: "Cargos e permissões",
  Types: "Tipos de atendimento",
  ChatbotConfig: "Chatbots",
  AutomationRules: "Regras de automação",
  PaymentMethods: "Formas de pagamento",
  DeliveryMethods: "Formas de entrega",
  AntifraudMethods: "Antifraude",
  Integrations: "Integrações",
  Categories: "Categorias",
  Coupons: "Cupons",
  Banners: "Banners",
  Contents: "Conteúdos",
  AwarenessStages: "Etapas dos níveis de consciência",
  PipelineStages: "Etapas do pipeline",
  WorkspaceLanguage: "Idiomas",
  Billing: "Plano e faturamento",
  NotificationsSettings: "Notificações",
  Permissions: "Permissões",
  Profile: "Meu perfil",
  Help: "Ajuda",
  Topbar: "Barra superior",
  Login: "Entrar",
  Signup: "Criar conta",
  VerifyEmail: "Verificar e-mail",
  SetPassword: "Definir senha",
  SetupWizard: "Configuração inicial",
  UserPlan: "Plano do usuário",
  WorkspaceSegment: "Tipo de operação",
  BpeInConversation: "Venda de passagem na conversa",
};

/** Famílias lógicas de tela — itens com a mesma família são agrupados juntos. */
const PAGE_TO_FAMILY = {
  ChatInbox: "chat",
  Conversation: "chat",
  ChatPopup: "chat-popup",
  Realtime: "chat",
  SalesPipeline: "sales-pipeline",
  AwarenessPipeline: "awareness",
  Products: "products",
  Surveys: "surveys",
  SurveyAnalytics: "survey-analytics",
  Campaigns: "campaigns",
  Dashboard: "dashboard",
  DashboardInsights: "dashboard",
  Clients: "clients",
  ClientDetails: "clients",
  SearchResults: "search-results",
  AgentsList: "agents",
  AgentForm: "agents",
  AgentAnalytics: "agents",
  Channels: "channels",
  Users: "users",
  BotFlowEditor: "users",
  Companies: "companies",
  Positions: "positions",
  Types: "types",
  ChatbotConfig: "chatbot",
  AutomationRules: "automation-rules",
  PaymentMethods: "payment-methods",
  DeliveryMethods: "delivery-methods",
  AntifraudMethods: "antifraud",
  Integrations: "integrations",
  Categories: "categories",
  Coupons: "coupons",
  Banners: "banners",
  Contents: "contents",
  AwarenessStages: "awareness-stages",
  PipelineStages: "pipeline-stages",
  WorkspaceLanguage: "workspace-i18n",
  Billing: "billing",
  NotificationsSettings: "notifications",
  Permissions: "permissions",
  Profile: "profile",
  Help: "help",
  Topbar: "topbar",
  BpeInConversation: "chat",
};

/** Rótulo por família quando há sub-telas distintas dentro do mesmo menu. */
const FAMILY_LABELS = {
  "chat-list": "Lista de conversas",
  "chat-conversation": "Dentro da conversa",
  "chat-realtime": "Atualização em tempo real",
  "chat-popup": "Janela separada (popup)",
  "sales-pipeline": "Pipeline de Vendas",
  awareness: "Níveis de consciência",
  products: "Produtos",
  surveys: "Pesquisas",
  "survey-analytics": "Análise da pesquisa",
  campaigns: "Campanhas",
  dashboard: "Dashboard",
  clients: "Clientes",
  "search-results": "Resultados da busca",
  agents: "Agentes de IA",
  channels: "Canais",
  users: "Usuários",
  companies: "Empresas",
  positions: "Cargos e permissões",
  types: "Tipos de atendimento",
  chatbot: "Chatbots",
  "automation-rules": "Regras de automação",
  "payment-methods": "Formas de pagamento",
  "delivery-methods": "Formas de entrega",
  antifraud: "Antifraude",
  integrations: "Integrações",
  categories: "Categorias",
  coupons: "Cupons",
  banners: "Banners",
  contents: "Conteúdos",
  "awareness-stages": "Etapas dos níveis de consciência",
  "pipeline-stages": "Etapas do pipeline",
  "workspace-i18n": "Idiomas",
  billing: "Plano e faturamento",
  notifications: "Notificações",
  permissions: "Permissões",
  profile: "Meu perfil",
  help: "Ajuda",
  topbar: "Barra superior",
};

/** Rota preferida para o botão "Ir para esta tela" por família. */
const FAMILY_ROUTE = {
  "chat-list": "/chat",
  "chat-conversation": "/chat",
  "chat-realtime": "/chat",
  "chat-popup": "/chat",
  "sales-pipeline": "/sales-pipeline",
  awareness: "/pipeline",
  products: "/products",
  surveys: "/surveys",
  "survey-analytics": "/surveys",
  campaigns: "/campaigns",
  dashboard: "/dashboard",
  clients: "/clients",
  agents: "/agents",
  channels: "/settings/channels",
  users: "/settings/users",
  companies: "/settings/companies",
  positions: "/settings/positions",
  types: "/settings/types",
  chatbot: "/settings/chatbot",
  "automation-rules": "/settings/automation-rules",
  "payment-methods": "/settings/payment-methods",
  "delivery-methods": "/settings/delivery-methods",
  antifraud: "/settings/antifraud-methods",
  integrations: "/settings/integrations",
  categories: "/settings/categories",
  coupons: "/settings/coupons",
  banners: "/settings/banners",
  contents: "/settings/contents",
  "awareness-stages": "/settings/awareness-stages",
  billing: "/settings/billing",
};

function rawRoute(item) {
  let r = String(item?.route || "").trim();
  if (r.startsWith("(") && r.endsWith(")")) r = r.slice(1, -1).trim();
  return r;
}

function normalizeRoute(route) {
  let r = String(route || "").trim();
  if (!r || r === "—" || r === "(n/a)" || r === "(api)" || r === "(api_only)" || r === "(config)") {
    return "";
  }
  if (r.startsWith("(") && r.endsWith(")")) r = r.slice(1, -1).trim();
  r = r.replace(/\/:[^/]+/g, "");
  if (r.endsWith("/") && r.length > 1) r = r.slice(0, -1);
  if (r === "/") return "/chat";
  return r;
}

function inventoryFamilyForItem(item) {
  const page = String(item?.page || "").trim();
  const route = rawRoute(item);

  if (page === "ChatPopup" || route.includes("popup-call")) return "chat-popup";
  if (page === "Realtime") return "chat-realtime";
  if (page === "Conversation" || page === "BpeInConversation") return "chat-conversation";
  if (page === "ChatInbox") {
    if (route.includes(":id")) return "chat-conversation";
    return "chat-list";
  }
  if (page === "SurveyAnalytics" || route.includes("/analytics")) return "survey-analytics";

  return PAGE_TO_FAMILY[page] || page || "other";
}

/** Chave estável para agrupar itens do inventário (substitui page|||route). */
export function inventoryGroupKey(item) {
  return inventoryFamilyForItem(item);
}

export function inventoryGroupLabel(item, { menuKey, menuLabel } = {}) {
  const family = inventoryGroupKey(item);
  const page = String(item?.page || "").trim();

  const familyLabel = FAMILY_LABELS[family];
  if (familyLabel) {
    if (menuKey && menuKey === family && menuLabel) return menuLabel;
    return familyLabel;
  }

  return PAGE_DISPLAY_LABELS[page] || page || "—";
}

export function inventoryGroupRoute(items) {
  const list = items || [];
  if (list.length === 0) return "";

  const family = inventoryGroupKey(list[0]);
  const preferred = FAMILY_ROUTE[family];
  if (preferred) return preferred;

  const candidates = list
    .map((it) => normalizeRoute(it.route))
    .filter((r) => r && r.startsWith("/"));
  if (candidates.length === 0) return "";

  candidates.sort((a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b));
  return candidates[0];
}

/**
 * Monta grupos de inventário para um bucket de menu.
 * Retorna [{ groupKey, displayLabel, route, count, items }]
 */
export function buildInventoryGroups(items, { menuKey, menuLabel } = {}) {
  const byGroup = new Map();
  (items || []).forEach((it) => {
    const key = inventoryGroupKey(it);
    const list = byGroup.get(key) || [];
    list.push(it);
    byGroup.set(key, list);
  });

  const ORDER = [
    "chat-list",
    "chat-conversation",
    "chat-realtime",
    "chat-popup",
    "sales-pipeline",
    "awareness",
    "products",
    "surveys",
    "survey-analytics",
    "campaigns",
    "dashboard",
    "clients",
    "agents",
    "channels",
    "users",
    "companies",
    "chatbot",
    "types",
    "integrations",
    "payment-methods",
    "delivery-methods",
    "antifraud",
  ];

  const orderIdx = (k) => {
    const i = ORDER.indexOf(k);
    return i === -1 ? 999 : i;
  };

  return Array.from(byGroup.entries())
    .sort((a, b) => orderIdx(a[0]) - orderIdx(b[0]) || a[0].localeCompare(b[0]))
    .map(([groupKey, groupItems]) => {
      const sample = groupItems[0];
      const displayLabel = inventoryGroupLabel(sample, { menuKey, menuLabel });
      return {
        groupKey,
        page: groupKey,
        displayLabel,
        route: inventoryGroupRoute(groupItems),
        count: groupItems.length,
        items: groupItems,
      };
    });
}

/** Quando há um único grupo ou o rótulo repete o menu, lista recursos sem sub-acordeão. */
export function shouldFlattenInventoryGroups(pageGroups, menuLabel) {
  if (!pageGroups || pageGroups.length === 0) return true;
  if (pageGroups.length === 1) return true;
  if (
    pageGroups.length === 2 &&
    menuLabel &&
    pageGroups.every((p) => normalizeLabel(p.displayLabel) === normalizeLabel(menuLabel))
  ) {
    return true;
  }
  return false;
}

function normalizeLabel(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function displayPageLabel(page, route, item) {
  if (item) return inventoryGroupLabel(item);
  const family = PAGE_TO_FAMILY[page];
  if (family && FAMILY_LABELS[family]) return FAMILY_LABELS[family];
  return PAGE_DISPLAY_LABELS[page] || page || "—";
}
